import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronDown,
  Download,
  FileDown,
  Image as ImageIcon,
  Loader2,
  MessageCircle,
  QrCode,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { toPng } from "html-to-image";

import useDashboard from "../../Services/DashboardServices/useDashboard";
import useClientConfig from "../../Services/ClientServices/ClientConfig/useClientConfig";

import chaafoLogo from "../../../Assets/chaafo.png";
import qrimage1 from "../../../Assets/BBQ & Grill_1x.webp";
import qrimage2 from "../../../Assets/Beverages_1x.webp";
import qrimage3 from "../../../Assets/Cake & Bakery_1x.webp";
import qrimage4 from "../../../Assets/Chinese & Asian_1x.webp";
import qrimage5 from "../../../Assets/Desserts & Ice Cream_1x.webp";
import qrimage6 from "../../../Assets/Fast Food_1x.webp";
import qrimage7 from "../../../Assets/Indian Food_1x.webp";
import qrimage8 from "../../../Assets/Pizza & Italian_1x.webp";
import qrimage9 from "../../../Assets/Sea Food_1x.webp";

const STATIC_QR_WHATSAPP_NUMBER = "919910254756";

const POSTER_BACKGROUNDS = [
  { id: "bbq", label: "BBQ & Grill", image: qrimage1 },
  { id: "beverages", label: "Beverages", image: qrimage2 },
  { id: "bakery", label: "Cake & Bakery", image: qrimage3 },
  { id: "chinese", label: "Chinese & Asian", image: qrimage4 },
  { id: "desserts", label: "Desserts & Ice Cream", image: qrimage5 },
  { id: "fast-food", label: "Fast Food", image: qrimage6 },
  { id: "indian", label: "Indian Food", image: qrimage7 },
  { id: "pizza", label: "Pizza & Italian", image: qrimage8 },
  { id: "sea-food", label: "Sea Food", image: qrimage9 },
];

const DEFAULT_BACKGROUND_ID = POSTER_BACKGROUNDS[0].id;

const cleanNumber = (value) => String(value || "").replace(/\D/g, "");

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Image source is missing."));
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load image: ${src}`));
    image.src = src;
  });

const imageToPngDataUrl = async (src) => {
  if (String(src || "").startsWith("data:image/png")) return src;

  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not supported in this browser.");

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png", 1);
};

const getImageSize = async (src) => {
  const image = await loadImage(src);
  return {
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
  };
};

const imageToWhitePngDataUrl = async (src) => {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");

  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas is not supported in this browser.");
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    if (pixels[index + 3] > 0) {
      pixels[index] = 255;
      pixels[index + 1] = 255;
      pixels[index + 2] = 255;
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png", 1);
};

const getCoverPlacement = ({
  imageWidth,
  imageHeight,
  frameWidth,
  frameHeight,
}) => {
  const imageRatio = imageWidth / imageHeight;
  const frameRatio = frameWidth / frameHeight;

  if (imageRatio > frameRatio) {
    const height = frameHeight;
    const width = height * imageRatio;
    return {
      x: (frameWidth - width) / 2,
      y: 0,
      width,
      height,
    };
  }

  const width = frameWidth;
  const height = width / imageRatio;
  return {
    x: 0,
    y: (frameHeight - height) / 2,
    width,
    height,
  };
};

const downloadBlob = (blob, fileName) => {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
};

const drawTextOnArc = ({
  pdf,
  text,
  centerX,
  centerY,
  radius,
  startAngle,
  endAngle,
  fontSize,
}) => {
  const characters = Array.from(text);
  const total = Math.max(characters.length - 1, 1);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(fontSize);
  pdf.setTextColor(255, 255, 255);

  characters.forEach((character, index) => {
    const angle = startAngle + ((endAngle - startAngle) * index) / total;
    const radians = (angle * Math.PI) / 180;
    const x = centerX + radius * Math.cos(radians);
    const y = centerY + radius * Math.sin(radians);

    pdf.text(character, x, y, {
      align: "center",
      angle: angle + 90,
    });
  });
};

const drawPdfArc = ({
  pdf,
  centerX,
  centerY,
  radius,
  startAngle,
  endAngle,
  steps = 90,
}) => {
  let previousPoint = null;

  for (let index = 0; index <= steps; index += 1) {
    const angle = startAngle + ((endAngle - startAngle) * index) / steps;
    const radians = (angle * Math.PI) / 180;
    const point = {
      x: centerX + radius * Math.cos(radians),
      y: centerY + radius * Math.sin(radians),
    };

    if (previousPoint) {
      pdf.line(previousPoint.x, previousPoint.y, point.x, point.y);
    }

    previousPoint = point;
  }
};

export default function WhatsappQrGenerateModal({
  open,
  onClose,
  clientId,
  clientName,
  defaultLoginNumber = "",
}) {
  const { generateWhatsappQRCode } = useDashboard();
  const { chaafoOnboarding } = useClientConfig();

  const [form, setForm] = useState({
    userid: "",
    loginNumber: "",
    phoneNoId: "",
    generatedMessage: "",
  });

  const [qrUrl, setQrUrl] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [selectedBackgroundId, setSelectedBackgroundId] = useState(
    DEFAULT_BACKGROUND_ID,
  );
  const [backgroundMenuOpen, setBackgroundMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stepText, setStepText] = useState("");

  const dropdownRef = useRef(null);
  const exportPosterRef = useRef(null);

  const selectedBackground = useMemo(
    () =>
      POSTER_BACKGROUNDS.find((item) => item.id === selectedBackgroundId) ||
      POSTER_BACKGROUNDS[0],
    [selectedBackgroundId],
  );

  useEffect(() => {
    if (!open) return;

    setForm({
      userid: clientId || "",
      loginNumber: defaultLoginNumber || "",
      phoneNoId: "",
      generatedMessage: "",
    });
    setQrUrl("");
    setQrDataUrl("");
    setSelectedBackgroundId(DEFAULT_BACKGROUND_ID);
    setBackgroundMenuOpen(false);
    setError("");
    setLoading(false);
    setPdfLoading(false);
    setStepText("");
  }, [open, clientId, defaultLoginNumber]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setBackgroundMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleChange = (key, value) => {
    if (key === "loginNumber") {
      setForm((previous) => ({
        ...previous,
        loginNumber: value,
        phoneNoId: "",
        generatedMessage: "",
      }));
      setQrUrl("");
      setQrDataUrl("");
      setError("");
      return;
    }

    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const handleClose = () => {
    if (loading || pdfLoading) return;
    onClose?.();
  };

  const generateLocalQr = async (generatedMessage) => {
    const whatsappLink = `https://wa.me/${STATIC_QR_WHATSAPP_NUMBER}?text=${encodeURIComponent(
      generatedMessage,
    )}`;

    return QRCode.toDataURL(whatsappLink, {
      errorCorrectionLevel: "H",
      margin: 1,
      width: 1000,
      color: {
        dark: "#000000",
        light: "#ffffff",
      },
    });
  };

  const submit = async (event) => {
    event.preventDefault();

    setError("");
    setQrUrl("");
    setQrDataUrl("");

    const userid = form.userid?.trim();
    const loginNumber = cleanNumber(form.loginNumber);

    if (!userid) {
      setError("Client User ID is missing.");
      return;
    }

    if (!loginNumber || loginNumber.length < 8) {
      setError("Enter a valid login number.");
      return;
    }

    try {
      setLoading(true);
      setStepText("Running onboarding...");

      const onboarded = await chaafoOnboarding({
        user_code: userid,
        phone_no: loginNumber,
      });

      const phoneNoId = onboarded?.phone_no_id;

      if (!phoneNoId) {
        throw new Error("Phone No ID not found from onboarding API.");
      }

      const generatedMessage = `order now from chaafo_${phoneNoId}`;

      setForm((previous) => ({
        ...previous,
        phoneNoId,
        generatedMessage,
      }));

      setStepText("Generating QR...");

      const result = await generateWhatsappQRCode({
        userid,
        whatsappNumber: STATIC_QR_WHATSAPP_NUMBER,
        message: generatedMessage,
      });

      if (!result?.success) {
        throw new Error(result?.message || "Failed to generate QR.");
      }

      setQrUrl(result?.qrUrl || "");
      setQrDataUrl(await generateLocalQr(generatedMessage));
    } catch (requestError) {
      console.error("WhatsApp QR generation failed:", requestError);
      setError(
        requestError?.response?.data?.responseMessage ||
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Failed to complete QR generation flow.",
      );
    } finally {
      setLoading(false);
      setStepText("");
    }
  };

  const downloadOnlyQr = () => {
    if (!qrDataUrl) return;

    const anchor = document.createElement("a");
    anchor.href = qrDataUrl;
    anchor.download = `${form.userid || "CLIENT"}_WHATSAPP_QR.png`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const downloadPosterPdf = async () => {
    if (!qrDataUrl) {
      setError("Generate the QR code before downloading the PDF.");
      return;
    }

    if (!exportPosterRef.current) {
      setError("Poster export view is not ready.");
      return;
    }

    try {
      setPdfLoading(true);
      setError("");

      // Ensure every image inside the hidden A4 poster is fully loaded.
      const images = Array.from(
        exportPosterRef.current.querySelectorAll("img"),
      );

      await Promise.all(
        images.map(
          (image) =>
            new Promise((resolve, reject) => {
              if (image.complete && image.naturalWidth > 0) {
                resolve();
                return;
              }

              image.onload = () => resolve();
              image.onerror = () =>
                reject(new Error(`Unable to load poster image: ${image.src}`));
            }),
        ),
      );

      // Allow the browser to finish SVG/text/layout rendering.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );

      /*
       * Export the exact same React preview DOM.
       * Fixed A4 pixel dimensions: 794 × 1123.
       * This keeps curved text, SVG line, dots, QR, logo and spacing identical.
       */
      const posterPng = await toPng(exportPosterRef.current, {
        width: 794,
        height: 1123,
        canvasWidth: 1588,
        canvasHeight: 2246,
        pixelRatio: 2,
        cacheBust: false,
        backgroundColor: "#ffffff",
        skipFonts: false,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      pdf.addImage(posterPng, "PNG", 0, 0, 210, 297, "chaafo-poster", "FAST");

      downloadBlob(
        pdf.output("blob"),
        `${form.userid || "CLIENT"}_CHAAFO_QR_POSTER.pdf`,
      );
    } catch (pdfError) {
      console.error("PDF download failed:", pdfError);
      setError(
        pdfError?.message || "Failed to download PDF. Please try again.",
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const resetQr = () => {
    setQrUrl("");
    setQrDataUrl("");
    setSelectedBackgroundId(DEFAULT_BACKGROUND_ID);
    setBackgroundMenuOpen(false);
    setError("");
    setForm((previous) => ({
      ...previous,
      phoneNoId: "",
      generatedMessage: "",
    }));
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/45 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 lg:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-2"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-2"
            >
              <Dialog.Panel className="w-full max-w-7xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-gray-100 bg-gradient-to-r from-emerald-50 via-white to-green-50 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-emerald-600 text-white shadow-sm">
                      <QrCode size={22} />
                    </div>
                    <div>
                      <Dialog.Title className="text-base font-bold text-gray-900">
                        Generate WhatsApp QR Poster
                      </Dialog.Title>
                      <p className="mt-1 text-xs text-gray-500">
                        {clientName || "Client"}
                        <span className="mx-1 text-gray-300">•</span>
                        <span className="font-mono font-semibold text-gray-700">
                          {clientId || "—"}
                        </span>
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading || pdfLoading}
                    className="grid h-9 w-9 place-items-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="grid gap-6 p-5 lg:grid-cols-[430px_minmax(0,1fr)]">
                  <div className="space-y-5">
                    <form
                      onSubmit={submit}
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-4 flex items-center gap-2">
                        <div className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                          <MessageCircle size={17} />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900">
                            QR Generation Details
                          </h3>
                          <p className="text-xs text-gray-500">
                            First onboarding API runs, then QR is generated.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <Field label="User ID">
                          <input
                            readOnly
                            value={form.userid}
                            className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none"
                          />
                        </Field>

                        <Field label="Login Number">
                          <input
                            value={form.loginNumber}
                            onChange={(event) =>
                              handleChange("loginNumber", event.target.value)
                            }
                            placeholder="9971407961"
                            inputMode="numeric"
                            className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                          />
                        </Field>

                        <Field label="Phone No ID">
                          <input
                            readOnly
                            value={form.phoneNoId}
                            placeholder="Generated after onboarding"
                            className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none"
                          />
                        </Field>

                        <Field label="QR Receiver Number">
                          <input
                            readOnly
                            value={STATIC_QR_WHATSAPP_NUMBER}
                            className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none"
                          />
                        </Field>

                        <Field label="Poster Background">
                          <div ref={dropdownRef} className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setBackgroundMenuOpen((previous) => !previous)
                              }
                              disabled={loading || pdfLoading}
                              className="flex h-12 w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 text-left outline-none transition hover:border-gray-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:opacity-60"
                            >
                              <img
                                src={selectedBackground.image}
                                alt={selectedBackground.label}
                                className="h-8 w-11 rounded-lg object-cover"
                              />
                              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-700">
                                {selectedBackground.label}
                              </span>
                              <ChevronDown
                                size={17}
                                className={`shrink-0 text-gray-400 transition ${
                                  backgroundMenuOpen ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {backgroundMenuOpen ? (
                              <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-72 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
                                {POSTER_BACKGROUNDS.map((item) => {
                                  const selected =
                                    item.id === selectedBackgroundId;

                                  return (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => {
                                        setSelectedBackgroundId(item.id);
                                        setBackgroundMenuOpen(false);
                                      }}
                                      className={`flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-gray-50 ${
                                        selected
                                          ? "bg-emerald-50 ring-1 ring-emerald-200"
                                          : ""
                                      }`}
                                    >
                                      <img
                                        src={item.image}
                                        alt={item.label}
                                        className="h-12 w-16 rounded-lg object-cover"
                                      />
                                      <span className="flex-1 text-sm font-semibold text-gray-700">
                                        {item.label}
                                      </span>
                                      {selected ? (
                                        <span className="rounded-full bg-emerald-600 px-2 py-1 text-[10px] font-bold text-white">
                                          Selected
                                        </span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </Field>

                        <Field label="Auto Generated Message">
                          <textarea
                            readOnly
                            rows={3}
                            value={form.generatedMessage}
                            placeholder="Generated after onboarding"
                            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 outline-none"
                          />
                          <p className="mt-1 text-[11px] text-gray-400">
                            Message is locked and cannot be edited.
                          </p>
                        </Field>
                      </div>

                      {error ? (
                        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-600">
                          {error}
                        </div>
                      ) : null}

                      <div className="mt-5 grid grid-cols-1 gap-2">
                        <button
                          type="submit"
                          disabled={loading || pdfLoading}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {loading ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                          {loading
                            ? stepText || "Generating..."
                            : "Generate WhatsApp QR"}
                        </button>

                        {qrDataUrl ? (
                          <>
                            <button
                              type="button"
                              onClick={downloadOnlyQr}
                              disabled={loading || pdfLoading}
                              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                            >
                              <Download size={16} />
                              Download Only QR
                            </button>

                            <button
                              type="button"
                              onClick={downloadPosterPdf}
                              disabled={loading || pdfLoading}
                              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 disabled:opacity-60"
                            >
                              {pdfLoading ? (
                                <Loader2 size={17} className="animate-spin" />
                              ) : (
                                <FileDown size={17} />
                              )}
                              {pdfLoading
                                ? "Creating PDF..."
                                : "Download Design PDF"}
                            </button>
                          </>
                        ) : null}

                        <button
                          type="button"
                          disabled={loading || pdfLoading}
                          onClick={resetQr}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
                        >
                          <RefreshCw size={15} />
                          Reset QR
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">
                          Poster Preview
                        </h3>
                        <p className="text-xs text-gray-500">
                          Preview and PDF use the same rounded Chaafo poster
                          design.
                        </p>
                      </div>

                      {qrDataUrl ? (
                        <button
                          type="button"
                          onClick={downloadPosterPdf}
                          disabled={pdfLoading}
                          className="inline-flex h-9 items-center gap-2 rounded-xl bg-blue-700 px-3 text-xs font-bold text-white hover:bg-blue-800 disabled:opacity-60"
                        >
                          {pdfLoading ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <FileDown size={14} />
                          )}
                          PDF
                        </button>
                      ) : null}
                    </div>

                    <div className="max-h-[78vh] overflow-auto rounded-2xl bg-gray-200 p-4">
                      <div className="flex min-w-full justify-center">
                        <ChaafoPosterPreview
                          qrUrl={qrDataUrl}
                          logoUrl={chaafoLogo}
                          backgroundImage={selectedBackground.image}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hidden fixed-size A4 poster used only for exact PDF export */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none fixed left-[-10000px] top-0 overflow-hidden"
                  style={{
                    width: "794px",
                    height: "1123px",
                  }}
                >
                  <div
                    ref={exportPosterRef}
                    style={{
                      width: "794px",
                      height: "1123px",
                    }}
                  >
                    <ChaafoPosterPreview
                      qrUrl={qrDataUrl}
                      logoUrl={chaafoLogo}
                      backgroundImage={selectedBackground.image}
                      exportMode
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-gray-600">
        {label}
      </label>
      {children}
    </div>
  );
}

function ChaafoPosterPreview({
  qrUrl,
  logoUrl,
  backgroundImage,
  exportMode = false,
}) {
  return (
    <div
      className={
        exportMode
          ? "relative h-[1123px] w-[794px] overflow-hidden bg-white"
          : "relative aspect-[210/297] w-full max-w-[850px] overflow-hidden bg-white shadow-xl"
      }
    >
      <img
        src={backgroundImage}
        alt="Poster background"
        className="absolute left-0 top-1/2 h-[140%] w-full max-w-none -translate-y-1/2 object-cover"
      />

      <div className="absolute inset-0" />

      {/* Top Chaafo circle */}
      <div className="absolute left-1/2 top-0 aspect-square w-[68%] -translate-x-1/2 -translate-y-[50%] rounded-full bg-gradient-to-b from-[#0867da] to-[#00459c]">
        <img
          src={logoUrl}
          alt="Chaafo"
          className="absolute bottom-[18%] left-1/2 w-[58%] max-w-none -translate-x-1/2 object-contain brightness-0 invert"
        />
      </div>

      {/* Blue panel with a true half-circle top */}
      <div className="absolute bottom-0 left-1/2 h-[75%] w-[75%] -translate-x-1/2 overflow-hidden rounded-t-[999px] bg-gradient-to-b from-[#0867da] via-[#0756be] to-[#00377c] text-white">
        {/* Circular scanner area */}

        <div className="absolute left-1/2 top-[0.5%] aspect-square w-[100%] -translate-x-1/2">
          <svg
            className="absolute inset-0 h-full w-full overflow-visible"
            viewBox="0 0 400 400"
            aria-hidden="true"
          >
            <defs>
              {/* 170 Radius walla shared arc path */}
              <path
                id="referenceScanTextArc"
                d="M 30 200 A 170 170 0 0 1 370 200"
              />
            </defs>

            {/* Top Curved Text - dominant-baseline="central" se text line ke CENTER par aligned hoga */}
            <text
              fill="white"
              fontSize="36"
              fontWeight="700"
              fontFamily="'Host Grotesk', sans-serif"
              letterSpacing="-0.5"
              dominantBaseline="central"
            >
              <textPath
                href="#referenceScanTextArc"
                startOffset="50%"
                textAnchor="middle"
              >
                SCAN NOW &amp; SAY HI
              </textPath>
            </text>

            {/* Line jo text ke CENTER heights (S aur HI) se direct jude (attach) gi */}
            <path
              d="M 50 125 A 170 170 0 1 0 350 130"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          {/* Inner White Scanner Circle */}
          <div className="absolute left-1/2 top-[53%] grid aspect-square w-[75%] -translate-x-1/2 -translate-y-1/2 place-items-center overflow-hidden rounded-full bg-white p-[11%]">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="QR Code"
                className="block h-full w-full max-w-none object-contain"
              />
            ) : (
              <div className="grid h-full w-full place-items-center text-center text-[clamp(8px,1.2vw,15px)] font-bold text-[#0059c1]">
                QR Preview
              </div>
            )}
          </div>
        </div>

        <div
          className="absolute left-0 right-0 top-[70%] text-center text-[clamp(17px,3.35vw,41px)] font-black leading-[1.12] tracking-tight"
          style={{ fontFamily: "'Host Grotesk', sans-serif" }}
        >
          <p>ORDER NOW</p>
          <p>INSTANTLY</p>
          <p>ON WHATSAPP</p>
        </div>

        <div
          className="absolute left-1/2 top-[90%] flex h-[6.2%] w-[91%] -translate-x-1/2 items-center justify-center rounded-full bg-white px-3 text-center text-[clamp(7px,1.35vw,16px)] font-bold text-[#004faa]"
          style={{ fontFamily: "'Host Grotesk', sans-serif" }}
        >
          Cooking Up with Smarter Restro Solutions
        </div>

        <div className="absolute bottom-[2.2%] left-1/2 w-[91%] -translate-x-1/2 border-t border-dashed border-white/90" />
      </div>
    </div>
  );
}
